import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Save } from "lucide-react";
import {
  ALL_CONTENT_MODULES,
  CONTENT_MODULES,
  DEFAULT_ARTICLE_CONFIG,
  DEFAULT_REGULAR_CONFIG,
  LIMIT_MENUS,
  LIMIT_WINDOWS,
  SETTING_KEYS,
  effectiveModules,
  type AccountDTO,
  type ArticleConfig,
  type BrandLimits,
  type LimitMenuKey,
  type LimitWindow,
  type RegularConfig,
} from "@app/shared";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { useToast } from "./Toast";

function msg(e: unknown): string {
  return e instanceof Error ? e.message : "Terjadi kesalahan";
}

const DAY_LABELS = ["Min", "Sen", "Sel", "Rab", "Kam", "Jum", "Sab"];

/** Weekday multi-toggle (0=Sun..6=Sat). */
function DayPicker({
  value,
  onChange,
}: {
  value: number[];
  onChange: (v: number[]) => void;
}) {
  const toggle = (d: number) =>
    onChange(
      value.includes(d) ? value.filter((x) => x !== d) : [...value, d].sort(),
    );
  return (
    <div className="flex flex-wrap gap-1.5">
      {DAY_LABELS.map((label, d) => (
        <button
          key={d}
          type="button"
          onClick={() => toggle(d)}
          className={
            "h-8 w-11 rounded-md border text-xs font-medium transition-colors " +
            (value.includes(d)
              ? "border-teal bg-teal/15 text-teal"
              : "border-slate-300 bg-white text-slate-500 hover:bg-slate-50")
          }
        >
          {label}
        </button>
      ))}
    </div>
  );
}

/** A collapsible section wrapper (native disclosure, no extra state). */
function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <details className="rounded-md border border-slate-200 bg-slate-50">
      <summary className="cursor-pointer select-none px-4 py-2.5 text-sm font-medium text-navy">
        {title}
      </summary>
      <div className="space-y-4 border-t border-slate-200 p-4">{children}</div>
    </details>
  );
}

/** Parse a comma/newline list into a trimmed non-empty string array. */
function parseList(s: string): string[] {
  return s
    .split(/[,\n]/)
    .map((x) => x.trim())
    .filter(Boolean);
}

/** Per-brand Reguler cadence editor (Setting `regular_config:<brand>`). */
export function CadenceEditor({ brandKey }: { brandKey: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const settingKey = SETTING_KEYS.regularConfig(brandKey);

  const query = useQuery({
    queryKey: ["setting", settingKey],
    queryFn: async ({ signal }): Promise<RegularConfig> => {
      try {
        const s = await api.setting(settingKey, signal);
        return s.value as RegularConfig;
      } catch {
        return DEFAULT_REGULAR_CONFIG;
      }
    },
  });

  const [cfg, setCfg] = useState<RegularConfig | null>(null);
  useEffect(() => {
    if (query.data) setCfg(query.data);
  }, [query.data]);

  const save = useMutation({
    mutationFn: (value: RegularConfig) => api.updateSetting(settingKey, value),
    onSuccess: () => {
      toast.success("Cadence disimpan");
      void qc.invalidateQueries({ queryKey: ["setting", settingKey] });
      void qc.invalidateQueries({ queryKey: ["settings"] });
    },
    onError: (e) => toast.error(msg(e)),
  });

  if (!cfg) return null;
  const set = (patch: Partial<RegularConfig>) => setCfg({ ...cfg, ...patch });

  return (
    <Section title="Cadence posting (Reguler)">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="space-y-1.5">
          <Label htmlFor={`ppd-${brandKey}`}>Post per hari</Label>
          <Input
            id={`ppd-${brandKey}`}
            type="number"
            min={1}
            max={3}
            value={cfg.postsPerDay}
            onChange={(e) =>
              set({ postsPerDay: Math.max(1, Math.min(3, Number(e.target.value) || 1)) })
            }
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`time-${brandKey}`}>Jam tayang</Label>
          <Input
            id={`time-${brandKey}`}
            type="time"
            value={cfg.suggestedTime}
            onChange={(e) => set({ suggestedTime: e.target.value })}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`gen-${brandKey}`}>Jam generate</Label>
          <Input
            id={`gen-${brandKey}`}
            type="time"
            value={cfg.generateTime ?? "06:00"}
            onChange={(e) => set({ generateTime: e.target.value })}
          />
        </div>
      </div>
      <p className="text-xs text-slate-500">
        <strong>Jam generate</strong> = waktu konten brand ini dibuat otomatis tiap
        hari (dibuat berbeda antar brand agar beban server tersebar).{" "}
        <strong>Jam tayang</strong> = jam posting yang disarankan untuk konten yang
        dihasilkan.
      </p>
      <div className="space-y-1.5">
        <Label>Hari aktif</Label>
        <DayPicker
          value={cfg.activeDays}
          onChange={(activeDays) => set({ activeDays })}
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={`pillars-${brandKey}`}>Rotasi pilar (pisah koma)</Label>
        <Input
          id={`pillars-${brandKey}`}
          placeholder="K1, K2, K3, K4"
          value={cfg.pillarRotation.join(", ")}
          onChange={(e) => set({ pillarRotation: parseList(e.target.value) })}
        />
      </div>
      <Button
        variant="primary"
        onClick={() => save.mutate(cfg)}
        disabled={save.isPending}
      >
        {save.isPending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Save className="h-4 w-4" />
        )}
        Simpan cadence
      </Button>
    </Section>
  );
}

/** Per-brand Article cadence/SEO editor (Setting `article_config:<brand>`). */
export function ArticleConfigEditor({ brandKey }: { brandKey: string }) {
  const qc = useQueryClient();
  const toast = useToast();
  const settingKey = SETTING_KEYS.articleConfig(brandKey);

  const query = useQuery({
    queryKey: ["setting", settingKey],
    queryFn: async ({ signal }): Promise<ArticleConfig> => {
      try {
        const s = await api.setting(settingKey, signal);
        return s.value as ArticleConfig;
      } catch {
        return DEFAULT_ARTICLE_CONFIG;
      }
    },
  });

  const [cfg, setCfg] = useState<ArticleConfig | null>(null);
  useEffect(() => {
    if (query.data) setCfg(query.data);
  }, [query.data]);

  const save = useMutation({
    mutationFn: (value: ArticleConfig) => api.updateSetting(settingKey, value),
    onSuccess: () => {
      toast.success("Konfigurasi artikel disimpan");
      void qc.invalidateQueries({ queryKey: ["setting", settingKey] });
      void qc.invalidateQueries({ queryKey: ["settings"] });
    },
    onError: (e) => toast.error(msg(e)),
  });

  if (!cfg) return null;
  const set = (patch: Partial<ArticleConfig>) => setCfg({ ...cfg, ...patch });
  const numOrNull = (v: string): number | null =>
    v.trim() === "" ? null : Number(v) || null;

  return (
    <Section title="Artikel & SEO">
      <div className="space-y-1.5">
        <Label htmlFor={`kw-${brandKey}`}>Keyword target (pisah koma)</Label>
        <Input
          id={`kw-${brandKey}`}
          placeholder="kata kunci 1, kata kunci 2"
          value={cfg.targetKeywords.join(", ")}
          onChange={(e) => set({ targetKeywords: parseList(e.target.value) })}
        />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <div className="space-y-1.5">
          <Label htmlFor={`len-${brandKey}`}>Panjang (kata)</Label>
          <Input
            id={`len-${brandKey}`}
            type="number"
            min={100}
            value={cfg.articleLengthWords}
            onChange={(e) =>
              set({ articleLengthWords: Number(e.target.value) || 0 })
            }
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`freq-${brandKey}`}>Artikel/minggu</Label>
          <Input
            id={`freq-${brandKey}`}
            type="number"
            min={0}
            value={cfg.frequencyPerWeek}
            onChange={(e) =>
              set({ frequencyPerWeek: Number(e.target.value) || 0 })
            }
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`topics-${brandKey}`}>Topik per riset</Label>
          <Input
            id={`topics-${brandKey}`}
            type="number"
            min={1}
            value={cfg.topicCount}
            onChange={(e) => set({ topicCount: Number(e.target.value) || 1 })}
          />
        </div>
      </div>
      <div className="space-y-1.5">
        <Label>Hari publish</Label>
        <DayPicker
          value={cfg.publishDays}
          onChange={(publishDays) => set({ publishDays })}
        />
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor={`cta-${brandKey}`}>CTA teks</Label>
          <Input
            id={`cta-${brandKey}`}
            value={cfg.ctaText}
            onChange={(e) => set({ ctaText: e.target.value })}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`ctaurl-${brandKey}`}>CTA URL</Label>
          <Input
            id={`ctaurl-${brandKey}`}
            placeholder="https://…"
            value={cfg.ctaUrl}
            onChange={(e) => set({ ctaUrl: e.target.value })}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`wpcat-${brandKey}`}>WP Category ID</Label>
          <Input
            id={`wpcat-${brandKey}`}
            type="number"
            value={cfg.wpCategoryId ?? ""}
            onChange={(e) => set({ wpCategoryId: numOrNull(e.target.value) })}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor={`wpauth-${brandKey}`}>WP Author ID</Label>
          <Input
            id={`wpauth-${brandKey}`}
            type="number"
            value={cfg.wpAuthorId ?? ""}
            onChange={(e) => set({ wpAuthorId: numOrNull(e.target.value) })}
          />
        </div>
      </div>
      <Button
        variant="primary"
        onClick={() => save.mutate(cfg)}
        disabled={save.isPending}
      >
        {save.isPending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Save className="h-4 w-4" />
        )}
        Simpan artikel
      </Button>
    </Section>
  );
}

/** Per-brand: daily auto-generation scheduler on/off + which menus are enabled. */
export function ModulesEditor({ brand }: { brand: AccountDTO }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [autoGenerate, setAutoGenerate] = useState(brand.autoGenerate);
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(effectiveModules(brand.modules)),
  );

  const save = useMutation({
    mutationFn: () =>
      api.updateBrand(brand.key, {
        autoGenerate,
        // "all selected" persists as [] (= all) so future modules auto-apply.
        modules:
          selected.size === ALL_CONTENT_MODULES.length ? [] : [...selected],
      }),
    onSuccess: () => {
      toast.success("Modul & scheduler disimpan");
      void qc.invalidateQueries({ queryKey: ["accounts"] });
    },
    onError: (e) => toast.error(msg(e)),
  });

  const toggle = (key: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  return (
    <Section title="Modul & Scheduler">
      <div className="flex items-center justify-between gap-3 rounded-md border border-slate-200 p-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-navy">
            Generate otomatis harian
          </p>
          <p className="text-xs text-slate-500">
            Saat OFF, brand ini tidak dijadwalkan generate otomatis (generate
            manual tetap bisa).
          </p>
        </div>
        <button
          type="button"
          onClick={() => setAutoGenerate((v) => !v)}
          aria-pressed={autoGenerate}
          className={
            "shrink-0 rounded-full px-4 py-1.5 text-sm font-semibold transition-colors " +
            (autoGenerate ? "bg-teal text-white" : "bg-slate-200 text-slate-600")
          }
        >
          {autoGenerate ? "ON" : "OFF"}
        </button>
      </div>

      <div className="space-y-1.5">
        <Label>Menu aktif</Label>
        <div className="flex flex-wrap gap-1.5">
          {CONTENT_MODULES.map((m) => {
            const on = selected.has(m.key);
            return (
              <button
                key={m.key}
                type="button"
                onClick={() => toggle(m.key)}
                aria-pressed={on}
                className={
                  "rounded-md border px-3 py-1.5 text-xs font-medium transition-colors " +
                  (on
                    ? "border-teal bg-teal/15 text-teal"
                    : "border-slate-300 bg-white text-slate-500 hover:bg-slate-50")
                }
              >
                {m.label}
              </button>
            );
          })}
        </div>
        <p className="text-xs text-slate-500">
          Menu yang bisa dibuka untuk brand ini di workspace.
        </p>
      </div>

      <Button
        variant="primary"
        onClick={() => save.mutate()}
        disabled={save.isPending || selected.size === 0}
      >
        {save.isPending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Save className="h-4 w-4" />
        )}
        Simpan modul
      </Button>
    </Section>
  );
}

/** A native select styled to match the Input component. */
const SELECT_CLS =
  "h-9 rounded-md border border-slate-300 bg-white px-2 text-sm text-navy focus:outline-none focus:ring-2 focus:ring-teal/40";

/**
 * Per-brand usage quotas: a max-per-window cap (or unlimited) for each generate
 * menu plus draft revision. Persisted to `Account.limits`; unlimited menus are
 * omitted from the saved object (absent ⇒ unlimited).
 */
export function LimitsEditor({ brand }: { brand: AccountDTO }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [limits, setLimits] = useState<BrandLimits>(() => ({ ...brand.limits }));

  const save = useMutation({
    mutationFn: () => {
      // Send only genuinely-capped menus; a null/absent max means unlimited.
      const clean: BrandLimits = {};
      for (const m of LIMIT_MENUS) {
        const l = limits[m.key];
        if (l && l.max != null) clean[m.key] = { max: l.max, window: l.window };
      }
      return api.updateBrand(brand.key, { limits: clean });
    },
    onSuccess: () => {
      toast.success("Limit & kuota disimpan");
      void qc.invalidateQueries({ queryKey: ["accounts"] });
    },
    onError: (e) => toast.error(msg(e)),
  });

  const setMenu = (key: LimitMenuKey, value: BrandLimits[LimitMenuKey]) =>
    setLimits((prev) => {
      const next = { ...prev };
      if (value === undefined) delete next[key];
      else next[key] = value;
      return next;
    });

  return (
    <Section title="Limit & Kuota">
      <p className="text-xs text-slate-500">
        Batasi berapa kali tiap menu boleh generate/revisi dalam satu periode
        (hari/minggu/bulan). Saat batas tercapai, brand menerima error sampai
        periode berikutnya. Biarkan <strong>Unlimited</strong> untuk tanpa batas.
      </p>
      <div className="space-y-2">
        {LIMIT_MENUS.map((m) => {
          const lim = limits[m.key];
          const limited = !!lim && lim.max != null;
          return (
            <div
              key={m.key}
              className="flex flex-wrap items-center gap-2 rounded-md border border-slate-200 bg-white p-2.5"
            >
              <p className="min-w-[9rem] flex-1 text-sm font-medium text-navy">
                {m.label}
              </p>
              <button
                type="button"
                onClick={() =>
                  setMenu(
                    m.key,
                    limited ? undefined : { max: 10, window: "day" },
                  )
                }
                aria-pressed={limited}
                className={
                  "shrink-0 rounded-full px-3 py-1 text-xs font-semibold transition-colors " +
                  (limited
                    ? "bg-teal text-white"
                    : "bg-slate-200 text-slate-600")
                }
              >
                {limited ? "Dibatasi" : "Unlimited"}
              </button>
              {limited && lim && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500">maks</span>
                  <Input
                    type="number"
                    min={0}
                    className="h-9 w-20"
                    value={lim.max ?? 0}
                    onChange={(e) =>
                      setMenu(m.key, {
                        max: Math.max(0, Number(e.target.value) || 0),
                        window: lim.window,
                      })
                    }
                  />
                  <select
                    className={SELECT_CLS}
                    value={lim.window}
                    onChange={(e) =>
                      setMenu(m.key, {
                        max: lim.max,
                        window: e.target.value as LimitWindow,
                      })
                    }
                  >
                    {LIMIT_WINDOWS.map((w) => (
                      <option key={w.key} value={w.key}>
                        {w.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <Button
        variant="primary"
        onClick={() => save.mutate()}
        disabled={save.isPending}
      >
        {save.isPending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Save className="h-4 w-4" />
        )}
        Simpan limit
      </Button>
    </Section>
  );
}
